/* import __COLOCATED_TEMPLATE__ from './sq-form-checkbox.hbs'; */
/**
## Field Checkbox

`SqFormCheckboxComponent` is a simple, atomic checkbox alongside an optional text
label. It's designed to have only one row of content at a time. It can be used
either within a standard Form Field combo or independently elsewhere. Generally if you
are rolling within a full Form Field context, just use the
[Field Checkboxes](/components/glass/sq-field-checkboxes.html) component.
You can also pass in custom handlebars that will be displayed after the label.

```example
@title Basic Example

@controller-properties
{
  sampleChecked: false,
  sampleCheckedTwo: false,
  actions: {
    sampleAction: function() {
      $('.action-output').text('Checkbox clicked');
    }
  }
}

@hbs
{{deprecated/sq-form-checkbox label="Test"
                   checked=sampleChecked
                   action=(action "sampleAction")}}

{{deprecated/sq-form-checkbox label="Test With Secondary Text"
                   labelSecondary="Lorem ipsum dolor sit amet, consectetur adipisicing elit."
                   checked=sampleCheckedTwo
                   action=(action "sampleAction")}}

{{deprecated/sq-form-checkbox label="Test With Secondary Text Checked"
                   labelSecondary="Lorem ipsum dolor sit amet, consectetur adipisicing elit."
                   checked=true
                   action=(action "sampleAction")}}

{{deprecated/sq-form-checkbox label="Test With Secondary Text Disabled"
                   labelSecondary="Lorem ipsum dolor sit amet, consectetur adipisicing elit."
                   checked=sampleCheckedTwo
                   disabled=true
                   action=(action "sampleAction")}}

{{deprecated/sq-form-checkbox label="Test With Secondary Text Disabled Checked"
                   labelSecondary="Lorem ipsum dolor sit amet, consectetur adipisicing elit."
                   checked=true
                   disabled=true
                   action=(action "sampleAction")}}

<p class="action-output"></p>
```

```example
@title Standalone Checkbox
@description Basic checkboxes are a single element, `form-checkbox__content`. Always add `form-checkbox__input` to represent the semantically proper input element, and `form-checkbox__check` immediately after for the check.

@hbs
<div class="form-checkbox__content">
  <input class="form-checkbox__input" id="checkbox0" type="checkbox" value="checkbox0">
  <div class="form-checkbox__check"></div>
</div>
```

```example
@title Checkbox Groupings
@description We run with one or multiple checkboxes in a group. Wrap with `form-input`, with multiple `form-checkbox` within. One oddball addition: add the class `form-field--with-checkboxes` to the `form-field` item. This is necessary to account for the extra large size of the checkbox.

@hbs
<div class="form-table">
  <div class="form-row">
    <div class="form-field form-field--with-checkboxes">
      <label class="form-field__label" for="selectExample">Sample Label</label>
      <div class="form-field__content">
        <label class="form-checkbox">
          <div class="form-checkbox__content">
            <input class="form-checkbox__input" id="checkbox1" type="checkbox" value="checkbox1">
            <div class="form-checkbox__check"></div>
          </div>
          <div class="form-checkbox__label">Checkbox Value 1</div>
        </label>
      </div>
    </div>
  </div>
  <div class="form-row">
    <div class="form-field form-field--with-checkboxes form-field--is-disabled">
      <label class="form-field__label" for="selectExample">Sample Label (Disabled)</label>
      <div class="form-field__content">
        <label class="form-checkbox">
          <div class="form-checkbox__content">
            <input class="form-checkbox__input" id="checkbox1" type="checkbox" value="checkbox1" disabled="disabled">
            <div class="form-checkbox__check"></div>
          </div>
          <div class="form-checkbox__label">Checkbox Value 1</div>
        </label>
      </div>
    </div>
  </div>
  <div class="form-row">
    <div class="form-field form-field--with-checkboxes form-field--is-read-only">
      <label class="form-field__label" for="selectExample">Sample Label (Read-Only)</label>
      <div class="form-field__content">
        <label class="form-checkbox">
          <div class="form-checkbox__content">
            <input class="form-checkbox__input" id="checkbox1" type="checkbox" value="checkbox1" disabled="disabled">
            <div class="form-checkbox__check"></div>
          </div>
          <div class="form-checkbox__label">Checkbox Value 1</div>
        </label>
      </div>
    </div>
  </div>
  <div class="form-row">
    <div class="form-field form-field--with-checkboxes">
      <label class="form-field__label" for="selectExample">Sample Label</label>
      <div class="form-field__content">
        <label class="form-checkbox">
          <div class="form-checkbox__content">
            <input class="form-checkbox__input" id="checkbox1" type="checkbox" value="checkbox1">
            <div class="form-checkbox__check"></div>
          </div>
          <div class="form-checkbox__label">Checkbox Value 1</div>
        </label>
        <label class="form-checkbox">
          <div class="form-checkbox__content">
            <input class="form-checkbox__input" id="checkbox2" type="checkbox" value="checkbox2">
            <div class="form-checkbox__check"></div>
          </div>
          <div class="form-checkbox__label">Checkbox Value 2</div>
        </label>
        <label class="form-checkbox">
          <div class="form-checkbox__content">
            <input class="form-checkbox__input" id="checkbox3" type="checkbox" value="checkbox3">
            <div class="form-checkbox__check"></div>
          </div>
          <div class="form-checkbox__label">Checkbox Value 3</div>
        </label>
      </div>
    </div>
  </div>
</div>
```

### Parameters
@param {Boolean} [checked=false] - Simple binding for if the checkbox is checked or not.
@param {Boolean} [disabled=false] - Disabled state for checkbox.
@param {Boolean} [indeterminate=false] - Display indeterminate style for checkbox.
@param {Boolean} [isHorizontal=false] - Display horizontal row style for checkbox.
@param {String} label - Text label that appears immediately to the right of the actual checkbox.
@param {String} labelSecondary - Optional secondary text, meant to accompany the label.
@param {String} ariaLabelId - Specify the id referencing the checkbox label element for aria-labelledby.
@param {Action} action - Fires when checkbox changes value, passes new value as an additional parameter.

Sass dependencies at: `app/assets/stylesheets/bridgeport/components/form-elements/formfield/_formfield-checkbox.sass`
*/

import { alias } from '@ember/object/computed';
import Component from '@ember/component';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  autofocus: false,
  isIndeterminate: alias('indeterminate'),
  isDisabled: alias('disabled'),
  isHorizontal: false,
  tagName: 'label',
  classNames: ['form-checkbox'],
  classNameBindings: ['isDisabled:form-checkbox--is-disabled', 'isHorizontal:form-checkbox--horizontal', 'isIndeterminate:form-checkbox--indeterminate'],
  ariaLabelId: 'sq-form-checkbox-label',
  change() {
    if (this.action) {
      this.action(this.get('checked'));
    }
  }
});